import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WaitlistCapture — reusable email capture component.
 *
 * Props:
 *   variant: "light" (white bg) | "dark" (transparent, for dark-bg sections)
 *   source: string passed to backend to track where signup came from
 */
const WaitlistCapture = ({ variant = 'light', source = 'landing' }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | already | error

  const isDark = variant === 'dark';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      const res = await axios.post(`${API}/waitlist/join`, { email: email.trim(), source });
      setStatus(res.data.already_registered ? 'already' : 'success');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success' || status === 'already') {
    return (
      <div
        data-testid="waitlist-success-msg"
        className={`flex items-center gap-2 text-sm font-medium ${isDark ? 'text-white' : 'text-primary'}`}
      >
        <CheckCircle className="w-4 h-4 shrink-0" />
        <span>{status === 'already' ? t('landing.waitlist.alreadyOn') : t('landing.waitlist.success')}</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full" data-testid="waitlist-form">
      <p className={`text-sm mb-3 ${isDark ? 'text-white/70' : 'text-slate-500'}`}>
        {t('landing.waitlist.label')}
      </p>
      <div className={`flex gap-2 p-1.5 rounded-xl border ${
        isDark
          ? 'bg-white/10 border-white/20 backdrop-blur-sm'
          : 'bg-white border-slate-200 shadow-sm'
      }`}>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('landing.waitlist.placeholder')}
          data-testid="waitlist-email-input"
          className={`flex-1 min-w-0 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-sm ${
            isDark ? 'text-white placeholder:text-white/50' : 'text-slate-900 placeholder:text-slate-400'
          }`}
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          data-testid="waitlist-submit-btn"
          className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
            isDark
              ? 'bg-white text-primary hover:bg-secondary disabled:opacity-60'
              : 'bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60'
          }`}
        >
          {status === 'loading' ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" />{t('landing.waitlist.submitting')}</>
          ) : (
            <><Send className="w-3.5 h-3.5" />{t('landing.waitlist.submit')}</>
          )}
        </button>
      </div>
      {status === 'error' && (
        <p className={`text-xs mt-2 ${isDark ? 'text-red-300' : 'text-red-500'}`}>
          {t('landing.waitlist.error')}
        </p>
      )}
    </form>
  );
};

export default WaitlistCapture;
